import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { Commission, Payout, TeamMember, ClientType } from "../types";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import moment from "moment";
import XanoAPI, { AgentCommission } from "../lib/XanoAPI";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { formatCurrency } from "../utils/format";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "../components/ui/use-toast";
import { Download, ChevronLeft, ChevronRight, History } from "lucide-react";
import { Link } from "react-router-dom";

const CommissionTracking: React.FC = () => {
  const auth = useAuth();
  const { currentUser } = auth;
  const teamMembers = currentUser?.user_agency?.[0]?.members || [];
  const [selectedAgent, setSelectedAgent] = useState<TeamMember | null>(
    teamMembers.find((member: TeamMember) => member.id === currentUser?.id) ||
      null
  );
  const [payoutAmount, setPayoutAmount] = useState<string>("");
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [agentCommissions, setAgentCommissions] = useState<AgentCommission[]>(
    []
  );
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [clients, setClients] = useState<ClientType[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingCommissionId, setProcessingCommissionId] = useState<
    string | null
  >(null);
  const [view, setView] = useState<"table" | "calendar" | "clients">("table");
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [processingClientId, setProcessingClientId] = useState<number | null>(
    null
  );

  const xanoApi = XanoAPI.getInstance();

  useEffect(() => {
    if (currentUser?.module_crm_plan) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get agent commissions
      const commissionData = await xanoApi.getAgentCommissions();
      console.log("Agent commissions:", commissionData);

      if (commissionData && commissionData.length > 0) {
        setAgentCommissions(commissionData);
      } else {
        setAgentCommissions([]);
      }

      // Get clients
      const clientsData = await xanoApi.getAllClients();
      if (clientsData && clientsData.items) {
        setClients(clientsData.items);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Error",
        description: "Failed to load commission data. Please try again.",
        variant: "destructive",
      });
      setAgentCommissions([]);
      setPayouts([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const agentCommissions = getAgentCommissions(selectedAgent?.id || 0);
    const csvContent = [
      ["Date", "Client", "Agent", "Amount", "Status"],
      ...agentCommissions.map((commission) => [
        new Date(commission.created_at).toLocaleDateString(),
        getClientName(commission.client_id),
        getAgentName(commission.user_id),
        commission.commission_amount.toString(),
        commission.status,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `commissions-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const getAgentCommissions = (userId: number) => {
    return agentCommissions.filter(
      (commission) => commission.user_id === userId
    );
  };

  const getClientName = (clientId: number) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      return `${client.firstName} ${client.lastName}`;
    }
    return `Client #${clientId}`;
  };

  const getAgentName = (userId: number) => {
    const agent = teamMembers.find(
      (m: { id: number; name: string }) => m.id === userId
    );
    if (agent) {
      return agent.name;
    }
    return "Unknown Agent";
  };

  const calculateAgentBalance = (userId: number) => {
    try {
      const userAgentCommissions = getAgentCommissions(userId);
      console.log("User agent commissions:", userAgentCommissions);

      // Only count approved commissions for the balance
      const totalCommissions = userAgentCommissions
        .filter((c) => c.status === "approved")
        .reduce((sum, c) => sum + c.commission_amount, 0);

      console.log("Total approved commissions:", totalCommissions);

      return totalCommissions;
    } catch (error) {
      console.error("Error calculating agent balance:", error);
      return 0;
    }
  };

  const handlePayoutSubmit = async () => {
    if (!selectedAgent || !payoutAmount) return;

    try {
      setProcessing(true);
      console.log("Creating payout", {
        agent: selectedAgent,
        amount: payoutAmount,
      });

      const userId = selectedAgent.id;
      const amount = parseFloat(payoutAmount);

      // Get approved commissions to pay out
      const approvedCommissions = getAgentCommissions(userId)
        .filter((c) => c.status === "approved")
        .map((c) => parseInt(c.id));

      console.log("Approved commissions to pay out:", approvedCommissions);

      if (approvedCommissions.length === 0) {
        toast({
          title: "No Approved Commissions",
          description: "There are no approved commissions to pay out.",
          variant: "destructive",
        });
        setProcessing(false);
        return;
      }

      // Create a payout via the API
      try {
        // Note: This endpoint might not be available yet. When backend is ready,
        // uncomment this code and implement proper API handling
        /*
        const result = await xanoApi.createPayout({
          user_id: userId,
          amount: amount,
          commissions_paid: approvedCommissions,
          description: `Payout for ${selectedAgent.name} - ${new Date().toLocaleDateString()}`
        });
        console.log("Payout creation result:", result);
        */

        // Until the API is ready, show a message indicating future functionality
        toast({
          title: "Payout Function Coming Soon",
          description: `This would create a payout of ${formatCurrency(amount)} for ${selectedAgent.name}`,
        });

        // Reload data to get the latest commission statuses
        await loadData();
      } catch (error) {
        console.error("API not yet implemented for payouts");
        toast({
          title: "API Not Ready",
          description: "Payout API functionality will be available soon.",
          variant: "destructive",
        });
      }

      // Clear the input
      setPayoutAmount("");
    } catch (error) {
      console.error("Error creating payout:", error);
      toast({
        title: "Error",
        description: `Error creating payout: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveCommission = async (commissionId: string) => {
    try {
      setProcessingCommissionId(commissionId);
      console.log("Approving commission", commissionId);

      const result = await xanoApi.approveAgentCommission(commissionId);
      console.log("Approval result:", result);

      // Show success message
      toast({
        title: "Success",
        description: "Commission approved successfully!",
      });

      // Refresh data
      await loadData();
    } catch (error) {
      console.error(`Error approving commission ${commissionId}:`, error);
      toast({
        title: "Error",
        description: `Error approving commission: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    } finally {
      setProcessingCommissionId(null);
    }
  };

  const handleApproveClientCommission = async (
    clientId: number,
    commissionAmount: number
  ) => {
    try {
      setProcessingClientId(clientId);
      console.log("Paying out client commission", {
        clientId,
        commissionAmount,
      });

      if (!commissionAmount || commissionAmount <= 0) {
        toast({
          title: "Invalid Amount",
          description: "Please enter a valid commission amount greater than 0.",
          variant: "destructive",
        });
        setProcessingClientId(null);
        return;
      }

      const result = await xanoApi.payOutClientCommission(
        clientId,
        commissionAmount
      );
      console.log("Client payout result:", result);

      if (result.success) {
        // Show success message
        toast({
          title: "Success",
          description: "Client commission paid out successfully!",
        });

        // Refresh data
        await loadData();
      } else {
        toast({
          title: "Error",
          description: "Failed to pay out client commission. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`Error paying out client commission ${clientId}:`, error);
      toast({
        title: "Error",
        description: `Error paying out client commission: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    } finally {
      setProcessingClientId(null);
    }
  };

  const renderCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const daysInMonth = endOfMonth.diff(startOfMonth, "days") + 1;
    const firstDayOfMonth = startOfMonth.day();
    const days = [];
    const agentData = getAgentCommissions(selectedAgent?.id || 0);

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-32 border-b border-r bg-white" />
      );
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = startOfMonth.clone().add(i - 1, "days");
      const dayCommissions = agentData.filter((commission) =>
        moment(commission.created_at).isSame(currentDate, "day")
      );
      const isToday = moment().isSame(currentDate, "day");

      days.push(
        <div
          key={i}
          className={`h-32 border-b border-r p-2 bg-white hover:bg-gray-50/50 transition-colors ${isToday ? "bg-primary/5" : ""}`}
        >
          <div className={`font-medium mb-1 ${isToday ? "text-primary" : ""}`}>
            {i}
          </div>
          <div className="space-y-1.5 max-h-[calc(100%-2rem)] overflow-y-auto">
            {dayCommissions.length > 0 ? (
              dayCommissions.map((commission) => (
                <div
                  key={commission.id}
                  className={`text-xs p-1.5 rounded shadow-sm border ${
                    commission.status === "approved"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : "bg-yellow-50 text-yellow-700 border-yellow-200"
                  }`}
                >
                  <div className="font-medium truncate">
                    {getClientName(commission.client_id)}
                  </div>
                  <div className="text-[10px] opacity-75">
                    {formatCurrency(commission.commission_amount)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-[10px] text-gray-400 italic">
                No commissions
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentMonth((prev) => prev.clone().subtract(1, "month"))
              }
              className="hover:bg-primary/5"
              disabled={currentMonth.isSame(
                moment().subtract(1, "year"),
                "month"
              )}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-semibold text-gray-900">
                {currentMonth.format("MMMM")}
              </h3>
              <h3 className="text-xl font-semibold text-gray-500">
                {currentMonth.format("YYYY")}
              </h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentMonth((prev) => prev.clone().add(1, "month"))
              }
              className="hover:bg-primary/5"
              disabled={currentMonth.isSame(moment().add(1, "year"), "month")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-50 border border-green-200" />
              <span className="text-gray-600">Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-50 border border-yellow-200" />
              <span className="text-gray-600">Pending</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(moment())}
              className="ml-2 hover:bg-primary/5"
            >
              Today
            </Button>
          </div>
        </div>
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="grid grid-cols-7 gap-px bg-gray-100">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="bg-white p-3 text-center font-medium text-gray-600"
              >
                {day}
              </div>
            ))}
            {days}
          </div>
        </div>
      </div>
    );
  };

  if (!currentUser?.module_crm_plan) {
    return (
      <div className="hidden flex-col md:flex w-full">
        <Header />
        <div className="flex-1 space-y-0 p-8 pt-6 container flex flex-col mt-10 w-full">
          <div>Access denied. CRM module required.</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="hidden flex-col md:flex w-full space-y-20">
      <Header />
      <div className="flex-1 mb-12 space-y-6 p-8 pt-6 container flex flex-col mt-10 w-full">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-primary">
            Commission Tracking
          </h1>
          <p className="text-muted-foreground text-lg">
            Track and manage commission payouts for your team members.
          </p>
        </div>

        {/* Select Agent and Create Payout sections are hidden */}
        {/* 
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <Card className="border-primary/20 mb-12">
            <CardHeader>
              <CardTitle className="text-xl text-primary">
                Select Agent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedAgent?.id.toString()}
                onValueChange={(value) => {
                  const agent = teamMembers?.find(
                    (m: TeamMember) => m.id === parseInt(value)
                  );
                  setSelectedAgent(agent || null);
                  loadData(); // Reload data when agent changes
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers?.map((member: TeamMember) => (
                    <SelectItem key={member.id} value={member.id.toString()}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card className="border-primary/20 mb-12">
            <CardHeader>
              <CardTitle className="text-xl text-primary">
                Create Payout
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Available Balance
                  </label>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(
                      calculateAgentBalance(selectedAgent?.id || 0)
                    )}
                  </p>
                </div>
                <div className="flex gap-4">
                  <Input
                    type="number"
                    placeholder="Enter payout amount"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={handlePayoutSubmit}
                    className="bg-primary hover:bg-primary/90"
                    disabled={
                      processing ||
                      !payoutAmount ||
                      parseFloat(payoutAmount) <= 0
                    }
                  >
                    {processing ? "Processing..." : "Create Payout"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        */}

        {/* Pending Commissions Table */}
        <Card className="border-primary/20 mb-8">
          <CardHeader>
            <CardTitle className="text-xl text-primary">
              Pending Commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center p-6">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentCommissions
                    .filter((commission) => commission.status === "pending")
                    .map((commission) => (
                      <TableRow key={commission.id}>
                        <TableCell>
                          {new Date(commission.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {getClientName(commission.client_id)}
                        </TableCell>
                        <TableCell>
                          {getAgentName(commission.user_id)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(commission.commission_amount)}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            {commission.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() =>
                              handleApproveCommission(commission.id)
                            }
                            disabled={processingCommissionId === commission.id}
                            className={
                              processingCommissionId === commission.id
                                ? "opacity-50"
                                : ""
                            }
                          >
                            {processingCommissionId === commission.id ? (
                              <span className="flex items-center">
                                <span className="h-4 w-4 mr-2 rounded-full border-2 border-t-transparent border-white animate-spin"></span>
                                Processing...
                              </span>
                            ) : (
                              "Payout"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  {agentCommissions.filter(
                    (commission) => commission.status === "pending"
                  ).length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-4 text-gray-500"
                      >
                        No pending commissions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary/20 mb-16">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl text-primary">
              Commission History
            </CardTitle>
            <div className="flex items-center gap-4">
              <Tabs
                defaultValue="table"
                className="w-full"
                onValueChange={(value) =>
                  setView(value as "table" | "calendar" | "clients")
                }
              >
                <TabsList>
                  <TabsTrigger value="table">Table</TabsTrigger>
                  <TabsTrigger value="calendar">Calendar</TabsTrigger>
                  <TabsTrigger value="clients">Client Approval</TabsTrigger>
                </TabsList>
              </Tabs>
              <Link to="/account/payout-history/">
                <Button variant="outline" size="sm">
                  <History className="h-4 w-4 mr-2" /> Payout History
                </Button>
              </Link>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center p-6">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : view === "table" ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getAgentCommissions(selectedAgent?.id || 0).map(
                    (commission) => (
                      <TableRow key={commission.id}>
                        <TableCell>
                          {new Date(commission.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {getClientName(commission.client_id)}
                        </TableCell>
                        <TableCell>
                          {getAgentName(commission.user_id)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(commission.commission_amount)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              commission.status === "approved"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {commission.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                  {getAgentCommissions(selectedAgent?.id || 0).length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center py-4 text-gray-500"
                      >
                        No commission history found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : view === "calendar" ? (
              renderCalendar()
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Client Commission Approval
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client Name</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Rollover Amount</TableHead>
                      <TableHead>Agent Commission Rate</TableHead>
                      <TableHead>Commission Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients
                      .filter(
                        (client) =>
                          client.agent_commission !== undefined &&
                          client.agent_commission !== null &&
                          client.agent_commission > 0
                      )
                      .map((client) => (
                        <TableRow key={client.id}>
                          <TableCell>
                            {client.firstName} {client.lastName}
                          </TableCell>
                          <TableCell>
                            {client.user_id
                              ? getAgentName(client?.user_id)
                              : "Unassigned"}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(client.rollover_amount || 0)}
                          </TableCell>
                          <TableCell>
                            {client.agent_commission && client.rollover_amount
                              ? `${((client.agent_commission / client.rollover_amount) * 100).toFixed(2)}%`
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(client.agent_commission || 0)}
                          </TableCell>
                          <TableCell>
                            {client.commission_approved ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                Approved
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                Pending
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {!client.commission_approved && (
                              <Button
                                size="sm"
                                onClick={() =>
                                  handleApproveClientCommission(
                                    client.id as number,
                                    client.agent_commission || 0
                                  )
                                }
                                disabled={
                                  processingClientId === client.id ||
                                  !client.agent_commission ||
                                  client.agent_commission <= 0
                                }
                                className={
                                  processingClientId === client.id
                                    ? "opacity-50"
                                    : ""
                                }
                              >
                                {processingClientId === client.id ? (
                                  <span className="flex items-center">
                                    <span className="h-4 w-4 mr-2 rounded-full border-2 border-t-transparent border-white animate-spin"></span>
                                    Processing...
                                  </span>
                                ) : (
                                  "Pay Out"
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    {clients.filter(
                      (client) =>
                        client.agent_commission !== undefined &&
                        client.agent_commission !== null &&
                        client.agent_commission > 0
                    ).length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center py-4 text-gray-500"
                        >
                          No clients with commission amounts found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CommissionTracking;
